/**
 * Relay section — the operational band of the site dashboard: one card per
 * configured relay, followed by the unused relays a tech has yet to set up.
 *
 * Unused relays are grouped so four of them share the footprint of one working
 * relay. A relay with nothing configured on it carries almost no information, so
 * it earns a quarter of a card rather than a whole one however much room the
 * stage has — which keeps a mostly-unconfigured site from reading as a wall of
 * empty cards.
 *
 * Reads plain-JS relay objects from the new odl-app contract (unit.relays[]).
 *
 * Contains:
 * - RelaySection — the dashboard section component (default export)
 */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import moment from 'moment';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useUser } from '@sophocles/app';
import ScopeContext from '@sophocles/app/ScopeContext';
import HtmlTooltip from '@sophocles/components/HtmlTooltip';
import { selectCapabilities } from '@sophocles/features/capabilities';
import { selectDefaultLoadTypeIdForCustomerType, selectRelayLoadTypes } from '@sophocles/features/reference';
import { resolveScopeCustomerTypeId } from '@sophocles/hooks/relayLoadTerm';
import { userPermCheck } from '@sophocles/utils/userPermissionUtils';
import CompactRelayCard from '@sophocles/views/sites/components/RelayCards/CompactRelayCard';
import RelayCard from '@sophocles/views/sites/components/RelayCards/RelayCard';
import DashboardCard from '@sophocles/views/sites/components/SiteDashboard/DashboardCard';
import {
  deriveRelayCardLayout,
  resolveRelayLoadType,
} from '@sophocles/views/sites/components/SiteDashboard/dashboardDerivations';
import DashboardSection from '@sophocles/views/sites/components/SiteDashboard/DashboardSection';
import RelayCarousel from '@sophocles/views/sites/components/SiteDashboard/RelayCarousel';

// The pack an unused-relay group fills inside its single unit.
const RELAY_SETUP_GROUP_ROWS = 2;
const RELAY_SETUP_GROUP_COLUMNS = 2;

function RelaySection({
  loading,
  siteId,
  unit,
  siteTimeZone,
  customer,
  params,
  relays = [],
  usesCalendar,
  isBillboard,
  hasCharting,
  curfew,
  siteIccid,
  nextSunriseRaw = null,
  nextSunsetRaw = null,
  onClickEditSchedule,
  canManageUnit = false,
  trailingContent = null,
}) {
  const { t } = useTranslation();
  const { user } = useUser();
  const capabilities = useSelector(selectCapabilities);
  const theme = useTheme();
  // On a phone the relay cards page horizontally as a carousel instead of
  // stacking into one tall column.
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'));

  // Relay pairing: a primary relay references its partner via secondary_relay_id
  // (a primitive id, per the contract). Resolve it to the relay object at render
  // (RelayCard reads relay.secondary_relay as an object), and skip rendering the
  // secondary on its own — it's shown as part of the primary's card.
  const relayById = useMemo(() => new Map(relays.map(r => [r.id, r])), [relays]);
  const { configuredRelays, unconfiguredRelayGroups } = useMemo(() => deriveRelayCardLayout(relays), [relays]);

  // Resolve load_type_id (a primitive, per the contract) to its reference
  // load-type row so RelayCard's load_type.name / load_type.id reads resolve.
  // A relay with no load_type_id falls back to the scope customer type's
  // default load type, so it renders as if it had that type.
  const loadTypes = useSelector(selectRelayLoadTypes);
  const loadTypeById = useMemo(() => new Map(loadTypes.map(loadType => [loadType.id, loadType])), [loadTypes]);

  const scope = useContext(ScopeContext);
  const customerTypeId = resolveScopeCustomerTypeId(scope);
  const defaultLoadTypeId = useSelector(selectDefaultLoadTypeIdForCustomerType(customerTypeId));

  const resolveRelay = relay => {
    const secondaryRelay = relay.secondary_relay_id != null ? (relayById.get(relay.secondary_relay_id) ?? null) : null;
    return {
      ...relay,
      load_type: resolveRelayLoadType(relay, loadTypeById, defaultLoadTypeId),
      secondary_relay: secondaryRelay
        ? {
            ...secondaryRelay,
            load_type: resolveRelayLoadType(secondaryRelay, loadTypeById, defaultLoadTypeId),
          }
        : null,
    };
  };

  const relayCards = configuredRelays.map(relay => ({
    key: relay.id,
    element: (
      <RelayCard
        loading={loading}
        siteId={siteId}
        unit={unit}
        siteTimeZone={siteTimeZone}
        customer={customer}
        params={params}
        usesCalendar={usesCalendar}
        relay={resolveRelay(relay)}
        relays={relays}
        isBillboard={isBillboard}
        hasCharting={hasCharting}
        curfew={curfew}
        siteIccid={siteIccid}
        nextSunriseRaw={nextSunriseRaw}
        nextSunsetRaw={nextSunsetRaw}
        onClickEditSchedule={onClickEditSchedule}
        hideOptions={!canManageUnit}
      />
    ),
  }));

  const canSetUpRelay = userPermCheck(user, 'pliny.add_relayload');
  const canSendRelayCommands = capabilities.can_send_command ?? false;

  const renderSetupCard = (relay, fill) => (
    <CompactRelayCard
      relay={resolveRelay(relay)}
      siteId={siteId}
      canSetUp={canSetUpRelay}
      canSendCommands={canSendRelayCommands}
      onSetup={() => onClickEditSchedule(relay.id, null, 'loads')}
      fill={fill}
    />
  );

  const setupGroupCards = unconfiguredRelayGroups.map(relayGroup => ({
    key: `unconfigured-relays-${relayGroup[0].id}`,
    pagerLabel: `${t('sites.unusedRelay', 'Unused Relay')}: ${relayGroup
      .map(relay => `R${relay.relay_number}`)
      .join(', ')}`,
    element: (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(relayGroup.length, 2)}, minmax(0, 1fr))`,
          gap: 2,
        }}
      >
        {relayGroup.map(relay => (
          <Box key={relay.id}>{renderSetupCard(relay, false)}</Box>
        ))}
      </Box>
    ),
  }));

  const carouselItems = [
    ...relayCards,
    ...setupGroupCards,
    ...(trailingContent ? [{ key: 'trailing-content', element: trailingContent }] : []),
  ];

  if (isMobile && carouselItems.length > 1) {
    return (
      <DashboardSection>
        {curfew?.enabled ? <CurfewBanner curfew={curfew} t={t} /> : null}
        <DashboardCard span="full">
          <RelayCarousel items={carouselItems} />
        </DashboardCard>
      </DashboardSection>
    );
  }

  return (
    <DashboardSection>
      {curfew?.enabled ? <CurfewBanner curfew={curfew} t={t} /> : null}

      {relayCards.map(card => (
        <DashboardCard key={card.key} span={1}>
          {card.element}
        </DashboardCard>
      ))}

      {unconfiguredRelayGroups.map(relayGroup => (
        // One unit holding a 2x2 pack, so four unused relays cost the same room
        // as one working one.
        <Box
          key={`unconfigured-relays-${relayGroup[0].id}`}
          sx={{
            gridColumn: 'span 1',
            minWidth: 0,
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(relayGroup.length, RELAY_SETUP_GROUP_COLUMNS)}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${RELAY_SETUP_GROUP_ROWS}, minmax(0, 1fr))`,
            gap: 2,
          }}
        >
          {relayGroup.map((relay, index) => {
            // An odd group's last card widens to close the gap its missing
            // partner would otherwise leave beside it.
            const isTrailingOddCard =
              relayGroup.length > 1 && relayGroup.length % 2 !== 0 && index === relayGroup.length - 1;
            return (
              <Box
                key={relay.id}
                sx={{
                  minWidth: 0,
                  display: 'flex',
                  '& > *': { flex: 1, minWidth: 0 },
                  ...(isTrailingOddCard && { gridColumn: 'span 2' }),
                }}
              >
                {renderSetupCard(relay, true)}
              </Box>
            );
          })}
        </Box>
      ))}

      {trailingContent && <DashboardCard span={1}>{trailingContent}</DashboardCard>}
    </DashboardSection>
  );
}

/**
 * @param {object} props
 * @param {object} props.curfew - the site's active curfew window
 * @param {Function} props.t    - i18next translator from the calling component
 */
function CurfewBanner({ curfew, t }) {
  return (
    <DashboardCard span="full">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'var(--md-inverse-surface)',
          color: 'var(--md-inverse-on-surface)',
          px: 3,
          py: 1.5,
          fontSize: '1.125rem',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        {t('sites.activeCurfew', 'Active Curfew')}:&nbsp;
        {moment(curfew.start_time, 'HH:mm:ss').format('h:mm A')} -{' '}
        {moment(curfew.end_time, 'HH:mm:ss').format('h:mm A')}
        &nbsp;
        <HtmlTooltip
          title={
            <div style={{ padding: '8px', maxWidth: '800px', backgroundColor: 'var(--surface-primary)' }}>
              <h5
                style={{
                  borderBottom: '1px solid var(--border-default)',
                  textAlign: 'center',
                  paddingBottom: '4px',
                }}
              >
                {t('sites.curfew', 'Curfew')}
              </h5>
              <div style={{ fontSize: '14px' }}>
                {t(
                  'sites.curfewDescription',
                  'Account administrators have set a curfew to block non-privileged users from scheduling and manually activating during this time frame.',
                )}
              </div>
            </div>
          }
        >
          <InfoOutlinedIcon sx={{ fill: 'var(--md-inverse-on-surface)' }} />
        </HtmlTooltip>
      </Box>
    </DashboardCard>
  );
}

export default RelaySection;
